import { Checkbox, makeStyles, Typography } from "@material-ui/core"
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers"

import ClientLocalTimeDisplay from "../molecules/ClientLocalTimeDisplay"
import { Tabs } from "../molecules/Tabs"
import TimePicker from "../molecules/TimePicker"
import { colors } from "../../utils/theme"

import { InitDaysProps, PickerTab } from "../../utils/types"
import dayjs, { Dayjs } from "dayjs"

import DayjsUtils from "@date-io/dayjs"
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date"
import { ClientFragment } from "../../generated/graphql"

interface SchedulerProps {
  client: ClientFragment
  pickerTab: PickerTab
  setPickerTab: (value: React.SetStateAction<PickerTab>) => void
  selectedDate: Dayjs | MaterialUiPickersDate
  handleDateChange: (
    value: React.SetStateAction<Dayjs | MaterialUiPickersDate>
  ) => void
  selectedTime: Dayjs | MaterialUiPickersDate
  handleTimeChange: (
    value: React.SetStateAction<Dayjs | MaterialUiPickersDate>
  ) => void
  days: InitDaysProps[]
  setDays: (value: React.SetStateAction<InitDaysProps[]>) => void
}

export const ScheduleDateTimeInputs = ({
  client,
  pickerTab,
  setPickerTab,
  selectedDate,
  handleDateChange,
  selectedTime,
  handleTimeChange,
  days,
  setDays,
}: SchedulerProps) => {
  const classes = useStyles()

  return (
    <div className={classes.pickerContainer}>
      {client.most_recent_timezone && (
        <div className={classes.clientLocalTimeContainer}>
          <ClientLocalTimeDisplay client={client} />
        </div>
      )}
      <Tabs
        ariaLabel="Recurring and one time tabs"
        tabsContainerClass={classes.pickerTabsContainer}
        tabs={[
          { label: PickerTab.ONE_TIME, className: classes.tabButton },
          {
            label: PickerTab.RECURRING,
            className: classes.tabButton,
          },
        ]}
        onChange={(tab) => setPickerTab(tab)}
        initialTab={pickerTab}
      >
        <MuiPickersUtilsProvider utils={DayjsUtils}>
          <KeyboardDatePicker
            autoOk
            variant="inline"
            format="MM/DD/YYYY"
            label="Enter Date"
            value={selectedDate}
            onChange={(date) => handleDateChange(date)}
            minDate={new Date()}
            minDateMessage="Date should not be before today"
            fullWidth
            inputVariant="outlined"
            KeyboardButtonProps={{ className: classes.focusVisible }}
          />
        </MuiPickersUtilsProvider>
        <div>
          <Typography>Days of the Week</Typography>
          <div className={classes.dayPicker}>
            {days.map((day) => (
              <Checkbox
                key={day.label}
                value={day.label}
                checked={day.selected}
                onChange={(e) => {
                  setDays((oldDays) => {
                    const newIndex = oldDays.findIndex(
                      (d) => d.label === day.label
                    )
                    oldDays[newIndex] = { ...day, selected: e.target.checked }
                    return [...oldDays]
                  })
                }}
                color="default"
                inputProps={{ "aria-label": day.value }}
                icon={
                  <Typography
                    component="span"
                    className={classes.dayCheckboxLabel}
                  >
                    {day.label}
                  </Typography>
                }
                checkedIcon={
                  <Typography
                    component="span"
                    className={classes.dayCheckboxLabel}
                  >
                    {day.label}
                  </Typography>
                }
                className={classes.dayCheckbox}
              />
            ))}
          </div>
        </div>
      </Tabs>
      <TimePicker
        label="Enter Time"
        defaultTime={
          dayjs(selectedDate).isValid() ? dayjs(selectedDate) : dayjs()
        }
        onTimeChange={handleTimeChange}
      />
    </div>
  )
}

const useStyles = makeStyles((theme) => ({
  pickerContainer: {
    border: `1px solid ${colors.neutral.clay}`,
    borderRadius: 12,
    padding: theme.spacing(5, 3, 4),
    position: "relative",
    margin: theme.spacing(3, 3, 0, 3),
    display: "grid",
    gap: theme.spacing(2),
  },
  clientLocalTimeContainer: {
    justifySelf: "center",
  },
  pickerTabsContainer: {
    // width offset for pickerContainer margin
    width: `calc(100% - ${theme.spacing(6)}px)`,
    position: "absolute",
    left: theme.spacing(3),
    top: -30,
    border: `4px solid ${colors.neutral.white}`,
    borderRadius: 150,
    backgroundColor: colors.neutral.white,
  },
  pickerBody: {
    display: "grid",
    gap: theme.spacing(4),
  },
  dayPicker: {
    paddingTop: theme.spacing(2),
    display: "grid",
    gridAutoFlow: "column",
    gridAutoColumns: "1fr",
  },
  dayCheckbox: {
    borderColor: colors.neutral.clay,
    borderWidth: 1,
    borderStyle: "solid",
    borderLeftWidth: 0,
    borderRadius: 0,
    "&:first-child": {
      borderLeftWidth: 1,
      borderTopLeftRadius: 4,
      borderBottomLeftRadius: 4,
    },
    "&:last-child": {
      borderTopRightRadius: 4,
      borderBottomRightRadius: 4,
    },
    "&:hover": {
      backgroundColor: colors.primary.ice_03,
    },
    "&.Mui-checked": {
      backgroundColor: colors.primary.ice,
    },
    "&.Mui-checked:hover": {
      backgroundColor: colors.primary.periwinkle,
    },
    "&:active": {
      backgroundColor: colors.primary.periwinkle,
    },
    "&.Mui-focusVisible": {
      border: `2px solid ${colors.primary.periwinkle}`,
    },
  },
  dayCheckboxLabel: {
    color: colors.primary.shadow,
    fontWeight: "bold",
  },
  focusVisible: {
    "&.Mui-focusVisible": {
      boxShadow: `0 0 0 2px ${colors.primary.periwinkle}`,
    },
  },
  tabButton: {
    backgroundColor: colors.primary.ice_03,
    opacity: 1,
    color: colors.primary.midnight,
    textTransform: "capitalize",
    minHeight: 50,
    "&.Mui-selected": {
      backgroundColor: colors.primary.ice,
    },
    "&:first-child": {
      borderTopLeftRadius: 100,
      borderBottomLeftRadius: 100,
    },
    "&:last-child": {
      borderTopRightRadius: 100,
      borderBottomRightRadius: 100,
    },
    "&.MuiTab-root": {
      minWidth: "unset",
    },
  },
}))
